import React from 'react';
import { Info, TrendingUp, Calendar, Zap, AlertTriangle, FileCheck, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import type { DPEProjection } from '@/utils/dpe-projections';
import { getEtiquetteColor } from '@/utils/dpe-projections';

interface EvolutionCoefficientElectriqueProps {
  dpeProjection: DPEProjection;
}

export const EvolutionCoefficientElectrique: React.FC<EvolutionCoefficientElectriqueProps> = ({
  dpeProjection
}) => {
  if (!dpeProjection) return null;

  const isPre2026 = dpeProjection.isDpePre2026;

  return (
    <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-green-50">
      <CardHeader>
        <div className="flex items-center">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
            <TrendingUp className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <CardTitle className="text-xl text-blue-900">
              📈 Évolution réglementaire - Chauffage électrique
            </CardTitle>
            <CardDescription className="text-blue-700">
              {isPre2026
                ? 'Amélioration de votre DPE grâce aux nouvelles règles de calcul'
                : 'Votre DPE utilise déjà les règles de calcul actuelles'
              }
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {isPre2026 && (
          <>
            {/* Encart d'explication pour DPE pré-2026 */}
            <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
              <div className="flex items-start space-x-3">
                <Info className="w-6 h-6 text-orange-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-orange-900 mb-2">
                    Pourquoi notre application affiche-t-elle des valeurs différentes de votre DPE papier ?
                  </h3>
                  <p className="text-orange-800 mb-2">
                    Votre DPE papier a été établi avec l'ancien coefficient électrique (2,3). Depuis le 1er janvier 2026,
                    le coefficient a été réduit à 1,9, ce qui valorise mieux les logements avec {dpeProjection.isMixedInstallation ? 'équipements électriques' : 'chauffage électrique'}.
                  </p>
                  <p className="text-orange-800 font-medium">
                    Notre application calcule avec le coefficient actuel (1,9). Ce n'est pas une erreur, c'est l'application
                    de la réglementation en vigueur !
                  </p>
                </div>
              </div>
            </div>

            {/* Message principal pour DPE pré-2026 - adapté selon installation mixte */}
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <div className="flex items-start space-x-3">
                <Zap className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {dpeProjection.isMixedInstallation
                      ? 'Votre logement bénéficie partiellement de l\'évolution réglementaire'
                      : 'Bonne nouvelle ! Votre logement bénéficie de l\'évolution réglementaire'}
                  </h3>
                  <p className="text-gray-700">
                    {dpeProjection.isMixedInstallation ? (
                      <>
                        Votre installation combine un chauffage non-électrique et des équipements électriques (eau chaude sanitaire, éclairage).
                        Seule la partie électrique bénéficie du nouveau coefficient 1,9, améliorant votre performance énergétique sans travaux.
                      </>
                    ) : (
                      <>
                        Votre chauffage électrique est maintenant mieux valorisé dans le calcul DPE depuis le 1er janvier 2026,
                        améliorant automatiquement votre performance énergétique sans aucun travaux.
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Message spécifique pour installation mixte avec détails */}
        {dpeProjection.isMixedInstallation && dpeProjection.mixedInstallationDetails && (
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-3">
              Décomposition de votre installation mixte
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center bg-white rounded p-2">
                <span className="text-gray-700">Chauffage non-électrique (inchangé)</span>
                <span className="font-semibold text-gray-900">Coefficient 1,0</span>
              </div>
              <div className="flex justify-between items-center bg-white rounded p-2">
                <span className="text-gray-700">ECS + Éclairage + Auxiliaires (amélioré)</span>
                <span className="font-semibold text-green-700">2,3 → 1,9</span>
              </div>
            </div>
            <p className="text-blue-800 text-sm mt-3">
              Le gain affiché ({Math.round(dpeProjection.gainPourcentage2026)}%) correspond uniquement à l'amélioration
              de la partie électrique de votre installation.
            </p>
          </div>
        )}

        {!isPre2026 && (
          /* Message pour DPE post-2026 */
          <div className="bg-white rounded-lg p-4 border border-green-200">
            <div className="flex items-start space-x-3">
              <Zap className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  Votre DPE utilise déjà le coefficient actuel de 1,9
                </h3>
                <p className="text-gray-700">
                  Votre diagnostic a été établi avec les règles en vigueur depuis le 1er janvier 2026.
                  Le chauffage électrique est déjà correctement valorisé avec le coefficient de 1,9
                  (au lieu de l'ancien coefficient de 2,3).
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Comparaison DPE - adaptée selon la date */}
        {isPre2026 ? (
          <div className="grid md:grid-cols-2 gap-4">
            {/* DPE Papier (ancien coefficient) */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">DPE papier</h4>
                <Badge variant="outline" size="sm">Coefficient 2,3</Badge>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Consommation</span>
                  <span className="font-semibold">
                    {Math.round(dpeProjection.consoPar_m2_Actuel)} kWh/m²/an
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Classe énergétique</span>
                  <Badge
                    energyClass={dpeProjection.etiquetteActuelle as any}
                    size="lg"
                  />
                </div>
              </div>
            </div>

            {/* Valeur actuelle (nouveau coefficient) */}
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-green-800">Valeur actuelle</h4>
                <Badge variant="success" size="sm">Coefficient 1,9</Badge>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-green-700">Consommation</span>
                  <span className="font-semibold text-green-800">
                    {Math.round(dpeProjection.consoPar_m2_2026)} kWh/m²/an
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-green-700">Classe énergétique</span>
                  <div className="flex items-center space-x-2">
                    <Badge
                      energyClass={dpeProjection.etiquette2026 as any}
                      size="lg"
                    />
                    {dpeProjection.classImprovement2026 && (
                      <span className="text-green-600 font-bold text-sm">↗️ Amélioration !</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Pour DPE post-2026: afficher seulement la valeur actuelle */
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">Valeur DPE</h4>
              <Badge variant="success" size="sm">Coefficient 1,9</Badge>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Consommation</span>
                <span className="font-semibold">
                  {Math.round(dpeProjection.consoPar_m2_2026)} kWh/m²/an
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Classe énergétique</span>
                <Badge
                  energyClass={dpeProjection.etiquette2026 as any}
                  size="lg"
                />
              </div>
            </div>
          </div>
        )}

        {/* Gains détaillés - seulement pour DPE pré-2026 */}
        {isPre2026 && (
          <>
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-green-200">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <Calendar className="w-5 h-5 text-green-500 mr-2" />
                Amélioration depuis le 1er janvier 2026
              </h4>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 mb-1">
                    -{Math.round(dpeProjection.gainEnergiePrimaire2026)} kWh/m²/an
                  </div>
                  <div className="text-sm text-gray-600">Économie de consommation</div>
                </div>

                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-1">
                    -{Math.round(dpeProjection.gainPourcentage2026)}%
                  </div>
                  <div className="text-sm text-gray-600">Réduction en %</div>
                </div>

                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-1">
                    {dpeProjection.classImprovement2026 ? 'Oui' : 'Maintien'}
                  </div>
                  <div className="text-sm text-gray-600">Amélioration classe</div>
                </div>
              </div>
            </div>

            {/* Section Attestation ADEME pour DPE pré-2026 */}
            <div className="bg-green-50 rounded-lg p-4 border-2 border-green-300">
              <div className="flex items-start space-x-3">
                <FileCheck className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-semibold text-green-900 mb-2">
                    Obtenez votre attestation officielle avec la nouvelle classe énergétique
                  </h4>
                  <p className="text-green-800 mb-3">
                    Une attestation gratuite et immédiate peut être obtenue auprès de l'ADEME pour faire valoir
                    votre nouvelle classe énergétique calculée avec le coefficient actuel de 1,9.
                  </p>
                  <a
                    href="https://observatoire-dpe-audit.ademe.fr/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Accéder à l'observatoire ADEME
                  </a>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Information prospective - pour tous les DPE */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
            <Info className="w-5 h-5 text-blue-600 mr-2" />
            Évolutions futures envisagées
          </h4>

          <div className="space-y-2 text-sm text-blue-800">
            <p>
              <strong>Informations prospectives :</strong> Le coefficient électrique pourrait encore
              baisser jusqu'à 1,3 à plus long terme, selon les décisions gouvernementales futures.
            </p>

            {dpeProjection.coefficientFuturTerme && (
              <div className="flex justify-between items-center bg-white rounded p-2 border border-blue-100">
                <span>Projection avec coefficient 1,3 :</span>
                <div className="flex items-center space-x-2">
                  <span className="font-semibold">
                    {Math.round(dpeProjection.consoPar_m2_Terme)} kWh/m²/an
                  </span>
                  <Badge energyClass={dpeProjection.etiquetteTerme as any} size="sm" />
                  {dpeProjection.classImprovementTerme && (
                    <span className="text-green-600 text-xs">↗️</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Timeline réglementaire - adaptée selon la date */}
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-3">
            Timeline des évolutions réglementaires
          </h4>

          <div className="space-y-3">
            {isPre2026 ? (
              <>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-gray-400 rounded-full mr-3"></div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-900">Avant 2026</span>
                      <span className="text-sm text-gray-600">Coefficient 2,3</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3 relative">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-green-700">Depuis le 1er janvier 2026 ✓</span>
                      <span className="text-sm text-green-600">Coefficient 1,9</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-blue-700">À plus long terme</span>
                      <span className="text-sm text-blue-600">Coefficient 1,3 (envisagé)</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3 relative">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-green-700">Actuellement</span>
                      <span className="text-sm text-green-600">Coefficient 1,9</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-blue-700">À plus long terme</span>
                      <span className="text-sm text-blue-600">Coefficient 1,3 (envisagé)</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Précautions */}
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-yellow-800 mb-1">Précautions d'usage</h4>
              <div className="text-sm text-yellow-700 space-y-1">
                {isPre2026 ? (
                  <>
                    <p>
                      • Le coefficient 1,9 est maintenant en vigueur depuis le 1er janvier 2026
                    </p>
                    <p>
                      • L'attestation ADEME est le document officiel pour faire valoir votre nouvelle classe énergétique
                    </p>
                    <p>
                      • Les futures évolutions du coefficient (1,3) restent à confirmer selon les décisions gouvernementales
                    </p>
                    <p>
                      • Cette amélioration réglementaire ne dispense pas d'optimiser la performance réelle de votre logement
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      • Votre DPE utilise déjà le coefficient actuel de 1,9
                    </p>
                    <p>
                      • Les futures évolutions du coefficient (1,3) restent à confirmer selon les décisions gouvernementales
                    </p>
                    <p>
                      • Cette valorisation ne dispense pas d'optimiser la performance réelle de votre logement
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions recommandées - adaptées selon la date */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-2">
            Actions recommandées
          </h4>
          <ul className="text-sm text-gray-700 space-y-1">
            {isPre2026 ? (
              <>
                {dpeProjection.classImprovement2026 && (
                  <li>• Obtenir votre attestation ADEME avec la nouvelle classe énergétique</li>
                )}
                <li>• Conserver cette analyse comme preuve de l'amélioration réglementaire</li>
                <li>• Faire valoir votre nouvelle classe énergétique auprès de votre copropriété ou lors d'une vente/location</li>
                <li>• Anticiper la revalorisation de votre bien immobilier</li>
                <li>• Continuer à optimiser la performance réelle pour maximiser les économies</li>
              </>
            ) : (
              <>
                <li>• Conserver cette analyse pour vos démarches immobilières</li>
                <li>• Suivre les évolutions réglementaires futures concernant le coefficient électrique</li>
                <li>• Continuer à optimiser la performance réelle pour maximiser les économies</li>
                <li>• Considérer des travaux d'amélioration énergétique pour aller au-delà de la valorisation réglementaire</li>
              </>
            )}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default EvolutionCoefficientElectrique;